import classNames from 'classnames'
import { Command as CommandPrimitive } from 'cmdk'
import { ReactNode, useMemo, useRef, useState } from 'react'
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '../../common/command'
import { Popover, PopoverAnchor, PopoverContent } from '../../common/popover'
import { cn } from '../../lib/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCaretUp, faCheck, faCircleNotch } from '@fortawesome/free-solid-svg-icons'

type Props<T extends string> = {
   selectedValue: T
   onSelectedValueChange: (value: T) => void
   searchValue: string
   onSearchValueChange: (value: string) => void
   items: { value: T; label: string; display?: ReactNode }[]
   isLoading?: boolean
   emptyMessage?: string
   placeholder?: string
   label?: ReactNode | string
   showSelectedValue?: boolean
   error?: string
   showErrorText?: boolean
   isTouched?: boolean
}

export default function AutoComplete<T extends string>({
   selectedValue,
   onSelectedValueChange,
   searchValue,
   onSearchValueChange,
   items,
   isLoading,
   emptyMessage = 'No items.',
   placeholder = 'Search...',
   label,
   showSelectedValue,
   error,
   showErrorText,
   isTouched,
}: Props<T>) {
   const [open, setOpen] = useState(false)
   const inputRef = useRef<HTMLInputElement>(null)
   const labels = useMemo(
      () =>
         items.reduce((acc, item) => {
            acc[item.value] = item.label
            return acc
         }, {} as Record<string, string>),
      [items],
   )

   const reset = () => {
      onSelectedValueChange('' as T)
      onSearchValueChange('')
   }

   const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (!e.relatedTarget?.hasAttribute('cmdk-list') && labels[selectedValue] !== searchValue) {
         reset()
      }
   }

   const onSelectItem = (inputValue: string) => {
      if (inputValue === selectedValue) {
         reset()
      } else {
         onSelectedValueChange(inputValue as T)
         onSearchValueChange(labels[inputValue] ?? '')
         setTimeout(() => {
            setOpen(false)
         }, 0) // Timeout to allow the click event to propagate before closing the popover
      }
   }

   return (
      <div className="flex items-center">
         <Popover
            open={open}
            onOpenChange={(val) => {
               try {
                  setOpen(val)
                  if (!val) {
                     setTimeout(() => {
                        const currentLabel = (labels && typeof labels === 'object' && selectedValue)
                           ? labels[selectedValue] || ''
                           : ''
                        const safeSearchValue = searchValue || ''

                        if (currentLabel !== safeSearchValue) {
                           reset()
                        }
                     }, 100)
                  }
               } catch (error) {
                  console.error('Error in onOpenChange:', error)
                  setOpen(false)
               }
            }}
         >
            <Command
               shouldFilter={false}
               className={cn('rounded-none mb-6', {
                  'has-error': error,
                  'has-success': !error && isTouched,
               })}
            >
               {label && (
                  <label htmlFor="search" className="inline-block mb-1.5 whitespace-nowrap">
                     {label}
                  </label>
               )}
               <div
                  className={cn('border border-[#ced4da] rounded relative', {
                     'border-[#a94442]': error,
                     'border-[#198754]': !error && isTouched,
                  })}
               >
                  <PopoverAnchor className="flex-1 flex items-center">
                     {showSelectedValue && selectedValue && !open && (
                        <span
                           className="text-neutral-400 pl-3 -mr-2"
                           onClick={() => {
                              inputRef.current?.focus()
                           }}
                        >
                           {selectedValue}&nbsp;&bull;
                        </span>
                     )}
                     <CommandPrimitive.Input
                        asChild
                        value={searchValue}
                        onValueChange={onSearchValueChange}
                        onKeyDown={(e) => setOpen(e.key !== 'Escape')}
                        onMouseDown={() => setOpen((open) => !!searchValue || !open)}
                        onFocus={() => setOpen(true)}
                        onBlur={onInputBlur}
                     >
                        <input
                           name="search"
                           ref={inputRef}
                           type="text"
                           placeholder={placeholder}
                           className=" bg-transparent outline-none rounded w-full h-full pl-3 pr-5 py-1.5 "
                        />
                     </CommandPrimitive.Input>
                  </PopoverAnchor>
                  <div
                     className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                     onClick={() => {
                        setOpen((open) => !open)
                     }}
                  >
                     <FontAwesomeIcon icon={open ? faCaretUp : faCaretDown} />
                  </div>
               </div>
               {error && showErrorText && <div className={cn('error-block text-sm mt-2')}>{error}</div>}

               {!open && <CommandList aria-hidden="true" className="hidden" />}
               <PopoverContent
                  asChild
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onInteractOutside={(e) => {
                     if (e.target instanceof Element && e.target.hasAttribute('cmdk-input')) {
                        e.preventDefault()
                     }
                  }}
                  className="w-[--radix-popover-trigger-width] p-0"
               >
                  <CommandList className="relative">
                     {isLoading && (
                        <CommandPrimitive.Loading>
                           <div className="absolute inset-0 bg-neutral-100/50 flex items-center justify-center z-[1]">
                              <FontAwesomeIcon icon={faCircleNotch} spin size="lg" />
                           </div>
                        </CommandPrimitive.Loading>
                     )}
                     {items.length > 0 ? (
                        <CommandGroup>
                           {items.map((option) => (
                              <CommandItem
                                 key={option.value}
                                 value={option.value}
                                 onMouseDown={(e) => e.preventDefault()}
                                 onSelect={onSelectItem}
                                 className="cursor-pointer"
                              >
                                 <FontAwesomeIcon
                                    icon={faCheck}
                                    className={cn(
                                       'h-4 w-4',
                                       selectedValue === option.value ? 'opacity-100' : 'opacity-0',
                                    )}
                                 />
                                 {option.display || option.label}
                              </CommandItem>
                           ))}
                        </CommandGroup>
                     ) : null}
                     {!isLoading ? <CommandEmpty>{emptyMessage ?? 'No items.'}</CommandEmpty> : null}
                  </CommandList>
               </PopoverContent>
            </Command>
         </Popover>
      </div>
   )
}
